import type { ItemStat, Meal, Symptom } from './types';
import {
  calculateGutBaseline,
  getTransitWindowConfig,
  inferTransitSpeedFromSymptoms,
  isFlareSymptom,
} from './correlationEngine';

export interface FlareContext {
  flares: Array<{ atMs: number; symptom: Symptom }>;
  windowHours: number;
  baselineRate: number;
}

const HOUR_MS = 3_600_000;
const SYMPTOM_ID_THRESHOLD = 5;

export function buildFlareContext(meals: Meal[], symptoms: Symptom[]): FlareContext {
  const windowHours = getTransitWindowConfig(inferTransitSpeedFromSymptoms(symptoms)).colonicMaxHours;
  const baseline = calculateGutBaseline(meals, symptoms);
  const flares = symptoms
    .filter((s) => isFlareSymptom(s, baseline))
    .map((symptom) => ({ atMs: new Date(symptom.timestamp_iso).getTime(), symptom }))
    .filter((f) => Number.isFinite(f.atMs))
    .sort((a, b) => a.atMs - b.atMs);
  const ctx: FlareContext = { flares, windowHours, baselineRate: 0 };
  if (meals.length > 0) {
    const flared = meals.filter((m) => mealFollowedByFlare(m, ctx).flared).length;
    ctx.baselineRate = flared / meals.length;
  }
  return ctx;
}

function symptomIds(s: Symptom): string[] {
  const ids = new Set<string>();
  for (const [id, value] of Object.entries(s.symptom_scores || {})) {
    if (value >= SYMPTOM_ID_THRESHOLD) ids.add(id);
  }
  if (s.bloating_score >= SYMPTOM_ID_THRESHOLD) ids.add('bloating');
  if (s.soreness_score >= SYMPTOM_ID_THRESHOLD) ids.add('stomach_pain');
  if (typeof s.stool_type === 'number' && (s.stool_type <= 2 || s.stool_type >= 6)) ids.add('stool_type');
  return [...ids];
}

export function mealFollowedByFlare(
  meal: Meal,
  ctx: FlareContext
): { flared: boolean; lagHours: number | null; symptoms: string[] } {
  const mealMs = new Date(meal.timestamp_iso).getTime();
  const maxMs = ctx.windowHours * HOUR_MS;
  let lagHours: number | null = null;
  const symptoms: string[] = [];
  if (!Number.isFinite(mealMs)) return { flared: false, lagHours, symptoms };
  for (const f of ctx.flares) {
    const diff = f.atMs - mealMs;
    if (diff > 0 && diff <= maxMs) {
      if (lagHours === null) lagHours = diff / HOUR_MS;
      symptoms.push(...symptomIds(f.symptom));
    }
  }
  return { flared: lagHours !== null, lagHours, symptoms };
}

export function confidenceFor(timesEaten: number, lift: number): ItemStat['confidence'] {
  if (timesEaten < 4) return 'low';
  if (timesEaten >= 8 && lift >= 1.5) return 'high';
  return 'medium';
}

export function memberPill(stat: ItemStat): 'Suspect' | 'Likely fine' | 'Unclear' {
  if (stat.lift >= 1.5 && stat.timesEaten >= 4) return 'Suspect';
  if (stat.lift <= 1.1) return 'Likely fine';
  return 'Unclear';
}

function median(values: number[]): number {
  const v = [...values].sort((a, b) => a - b);
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

export function computeItemStat(name: string, meals: Meal[], ctx: FlareContext): ItemStat {
  const lags: number[] = [];
  const counts = new Map<string, number>();
  let flared = 0;
  for (const meal of meals) {
    const r = mealFollowedByFlare(meal, ctx);
    if (!r.flared) continue;
    flared++;
    if (r.lagHours !== null) lags.push(r.lagHours);
    for (const id of r.symptoms) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const timesEaten = meals.length;
  const rate = timesEaten > 0 ? flared / timesEaten : 0;
  const lift = ctx.baselineRate > 0 ? Math.round((rate / ctx.baselineRate) * 10) / 10 : 0;
  const mainSymptoms = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 2)
    .map(([id]) => id);
  return {
    name,
    timesEaten,
    timesFollowedByFlare: flared,
    lift,
    typicalLagHours: lags.length ? Math.round(median(lags)) : null,
    mainSymptoms,
    confidence: confidenceFor(timesEaten, lift),
  };
}
